import {
  createContext,
  createElement,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

interface RadioGroupContext {
  name?: string;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
}

const GroupContext = createContext<RadioGroupContext | null>(null);

interface RadioGroupProps {
  name?: string;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

/** `<mat-radio-group>` (a real custom element, so the legacy `mat-radio-group {}` rules apply): owns the selected value; its radios read and set it. */
export function RadioGroup({ name, value, onChange, disabled, className = '', children }: RadioGroupProps) {
  return (
    <GroupContext.Provider value={{ name, value, disabled, onChange }}>
      {createElement(
        'mat-radio-group',
        { className: `mat-mdc-radio-group ${className}`.trim(), role: 'radiogroup' },
        children,
      )}
    </GroupContext.Provider>
  );
}

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'children'> {
  value?: unknown;
  children?: ReactNode;
  hostClassName?: string;
}

/** `<mat-radio-button value>`: inside a group it follows the group; standalone it uses `checked`/`onChange`. */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, children, hostClassName = '', className = '', ...rest },
  ref,
) {
  const group = useContext(GroupContext);
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const checked = group ? Object.is(group.value, value) : Boolean(rest.checked);
  const disabled = group?.disabled || rest.disabled;

  const host = ['mat-mdc-radio-button', 'mat-accent', checked && 'mat-mdc-radio-checked', hostClassName]
    .filter(Boolean)
    .join(' ');

  // Same DOM as MatRadioButton (host element, touch target, native control, background, persistent ripple, label).
  return createElement(
    'mat-radio-button',
    { className: host },
    <div className="mdc-form-field">
      <div className={`mdc-radio${disabled ? ' mdc-radio--disabled' : ''}`}>
        <div className="mat-mdc-radio-touch-target"></div>
        <input
          {...rest}
          id={inputId}
          ref={ref}
          type="radio"
          name={group?.name ?? rest.name}
          className={`mdc-radio__native-control ${className}`.trim()}
          checked={checked}
          disabled={disabled}
          onChange={(event) => {
            if (group) group.onChange(value);
            rest.onChange?.(event);
          }}
        />
        <div className="mdc-radio__background">
          <div className="mdc-radio__outer-circle"></div>
          <div className="mdc-radio__inner-circle"></div>
        </div>
        <div className="mat-ripple mat-radio-ripple mat-mdc-focus-indicator">
          <div className="mat-ripple-element mat-radio-persistent-ripple"></div>
        </div>
      </div>
      <label className="mdc-label" htmlFor={inputId}>
        {children}
      </label>
    </div>,
  );
});
